import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { faDownload, faEye, faFloppyDisk, faPencil, faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { DateTimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { MultiSelect, MultiSelectTree } from '@progress/kendo-react-dropdowns';
import dayjs from 'dayjs';
import { Spinner } from 'react-bootstrap';
import styled from 'styled-components';

import { DynamicFormFieldModel, DynamicFormModel, FileModel, TagModel, WorklistViewModel } from 'models';

import { Field, Form, FormElement, FormStates, FormValidatorsService } from 'core/forms';
import { NotificationsService } from 'core/notifications';
import { Accordion, Button, ButtonLooks, ButtonVariants, ComponentSizes, DateTimePicker, Icon, Input } from 'core/ui';

import { useApiClient } from 'features/api';
import FileIcon from 'features/exam/assets/exam.svg?react';
import { TagsField } from 'features/tags/fragments/TagsField';

import { PreparedFile } from './PatientFiles.types';
import { FileThumbnailDisplay } from './PatientFilesHelperComponents';

// -----------------------------------
// Props Interface
// -----------------------------------
interface PatientFilePreviewEditProps {
  selectedFile: FileModel | null;
  onView: (file: FileModel) => void; // Callback to open full view modal
  onDownload: (file: FileModel) => void; // Callback to download file
  onDelete: (file: FileModel) => void; // Callback to delete file
  onRename: (file: FileModel, newName: string) => Promise<void>; // Callback to rename file, returns promise for async handling
  onUpdateDescription: (file: FileModel, description: string) => Promise<void>;
  onUpdateTimestamps: (file: FileModel, captureTime: Date | null | undefined, eventTime: Date | null | undefined) => Promise<void>; // Use Date | null | undefined
  allTagsFlat: TagModel[]; // Use passed merged flat tags
  tagIdToTagMap: Map<number, TagModel>; // Use passed merged ID map
  includeThumbnail?: boolean;
  showFileDetails?: boolean; // Whether to show the file details section
  showThumbnailGallery?: boolean; // Whether to show thumbnail gallery at bottom
  showViewButton?: boolean; // Whether to show the View button in actions - hide in gallery mode
  // ADD: New props for hierarchical tags and update handler
  allHierarchicalTags: TagModel[];
  onUpdateTags: (fileId: number, newTagIds: number[]) => Promise<void>;
  // ADD: Prop for indicating if tags are still loading from parent
  areTagsLoading?: boolean;
  // Add other props as needed, e.g., onEdit, api client
  formSubmissions: SubmissionModel[];
  areFormSubmissionsLoading: boolean;
  onFormUpdated: () => Promise<void>;
  // Worklist props for context
  selectedWorklist?: WorklistViewModel | null;
  allWorklists?: WorklistViewModel[] | null;
  // Readonly mode for share users
  isReadOnly?: boolean;
}

// -----------------------------------
// Styled Components
// -----------------------------------
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  height: 100%; /* Ensure it takes full height of its grid cell */
  overflow: hidden; /* Prevent container itself from scrolling */
`;

const PreviewContent = styled.div`
  flex-grow: 1;
  overflow-y: auto; /* Allow content scrolling */
  padding: 0.5em;
  /* Ensure padding doesn't break flexbox height calculation */
  box-sizing: border-box;
`;

const ThumbnailSection = styled.div`
  position: relative; /* Added for view button positioning */
  width: 100%;
  height: 250px; /* Fixed height for thumbnail area */
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  border-radius: 4px;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ViewIconButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  color: #333;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;

  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
`;

const MetadataSection = styled.div`
  margin-bottom: 24px;
`;

const MetadataTitle = styled.h5`
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
`;

const MetadataItem = styled.div<{ alignCenter?: boolean }>`
  color: #333;
  line-height: 1.8em; /* Reduced for condensed view */
  align-items: ${({ alignCenter = true }) => (alignCenter ? 'center' : 'flex-start')};

  strong {
    color: #555;
    margin-right: 1em; /* Reduced for condensed view */
    min-width: 80px; /* Align keys */
    display: inline-block;
    flex-shrink: 0; /* Prevent label from shrinking */
  }

  span,
  input,
  .k-datetimepicker {
    word-break: break-word;
    flex-grow: 1;
    min-width: 0;
  }
`;

const EditIconWrapper = styled.span`
  margin-left: 8px;
  cursor: pointer;
  color: #007bff;
  &:hover {
    color: #0056b3;
  }
  align-self: center; /* Keep icon vertically centered */
`;

const NameEditContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  width: 100%;
`;

const ActionsSection = styled.div`
  margin-top: 16px; /* Reduced space */
  padding-top: 16px; /* Reduced space */
  border-top: 1px solid #e8e8e8;
  display: flex;
  gap: 10px; /* Space between buttons */
  justify-content: flex-start; /* Align buttons left */
`;

const EditPlaceholder = styled.div`
  font-style: italic;
  color: #6c757d;
  font-size: 0.9rem;
  text-align: center;
  padding: 20px 0; /* Add some padding */
`;

// Styled Accordion for Tags
const StyledTagAccordion = styled(Accordion)`
  .accordion-item {
    background: transparent;
    border: none;
    box-shadow: none;
    margin: 0 0 16px 0;
    border-bottom: 1px solid #eee;

    .accordion-header {
      padding: 8px 0; /* Adjust padding */
      border-bottom: none; /* Remove double border */
      h2 button {
        // Target the button inside the header
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
      }
    }

    .accordion-body {
      padding: 10px 0; /* Adjust padding */
    }
  }
`;

const TimeEditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-grow: 1;
`;

// --- NEW: Description Section Styles
const StyledTextArea = styled(Input).attrs({ as: 'textarea' })`
  min-height: 80px; /* Or other desired height */
  resize: vertical;
  width: 100%;
`;
// -----------

// ADDED: Styled Accordion for Forms
const StyledFormAccordion = styled(Accordion)`
  .accordion-item {
    background: transparent;
    border: none;
    box-shadow: none;
    margin: 0 0 16px 0;
    border-bottom: 1px solid #eee;

    .accordion-header {
      padding: 8px 0; /* Adjust padding */
      border-bottom: none; /* Remove double border */
      h2 button {
        // Target the button inside the header
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
      }
    }

    .accordion-body {
      padding: 10px 0; /* Adjust padding */
    }
  }
`;

const FormHistoryItem = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 16px;
  background-color: #fff;
`;

const FormHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
`;

const FormHistoryContent = styled.div`
  padding: 12px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
`;

const FormFieldItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;

  strong {
    margin-bottom: 4px;
    font-weight: 600;
    color: #555;
  }

  input,
  textarea {
    width: 100%;
  }
`;

// -----------------------------------
// Component
// -----------------------------------
const TAGS_ACCORDION_KEY = 'tags';
const FORMS_ACCORDION_KEY = 'forms';

// Type extension for FileModel with description
interface FileModelWithDescription extends FileModel {
  description?: string | null;
}

// --- NEW: Interface for form submissions with ID ---
export interface SubmissionModel extends DynamicFormModel {
  id: number;
  dateSubmitted: string;
}

export const PatientFilePreviewEdit: React.FC<PatientFilePreviewEditProps> = ({
  selectedFile,
  onView,
  onDownload,
  onDelete,
  onRename,
  onUpdateDescription,
  onUpdateTimestamps,
  allTagsFlat,
  tagIdToTagMap,
  includeThumbnail,
  showFileDetails = true,
  showThumbnailGallery = true,
  showViewButton = true,
  allHierarchicalTags,
  onUpdateTags,
  areTagsLoading,
  formSubmissions: formSubmissionsFromProps,
  areFormSubmissionsLoading,
  onFormUpdated,
  selectedWorklist,
  allWorklists,
  isReadOnly = false,
}) => {
  const apiClient = useApiClient();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editableName, setEditableName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editableDescription, setEditableDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  const [fieldSelectionValue, setFieldSelectionValue] = useState<TagModel[]>([]);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [activeKeys, setActiveKeys] = useState<string[]>([TAGS_ACCORDION_KEY, FORMS_ACCORDION_KEY]); // State for accordion - expand both by default

  // State for timestamp editing
  const [isEditingCaptureTime, setIsEditingCaptureTime] = useState(false);
  const [editableCaptureTime, setEditableCaptureTime] = useState<Date | null>(null);
  const [isSavingCaptureTime, setIsSavingCaptureTime] = useState(false);

  const [isEditingEventTime, setIsEditingEventTime] = useState(false);
  const [editableEventTime, setEditableEventTime] = useState<Date | null>(null);
  const [isSavingEventTime, setIsSavingEventTime] = useState(false);

  const [formSubmissions, setFormSubmissions] = useState<SubmissionModel[]>(formSubmissionsFromProps);
  const [historyVisibleFor, setHistoryVisibleFor] = useState<string | null>(null);

  const [editingFormState, setEditingFormState] = useState<SubmissionModel | null>(null);
  const originalFormState = React.useRef<DynamicFormModel | null>(null);
  const [formSaveInProgress, setFormSaveInProgress] = useState(false);

  // For TagsField
  const [searchTerm, setSearchTerm] = useState('');

  const thisFilesSubmissions = useMemo(() => {
    if (!formSubmissionsFromProps || !selectedFile) {
      return [];
    }
    // Sort by date descending
    return formSubmissionsFromProps
      .filter((s) => s.entityId === selectedFile.id)
      .sort((a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime());
  }, [formSubmissionsFromProps, selectedFile]);

  // Group submissions by form name to handle history
  const groupedSubmissions = useMemo(
    () =>
      thisFilesSubmissions.reduce<Record<string, SubmissionModel[]>>((acc, submission) => {
        const formName = submission.form?.name ?? 'Unnamed Form';
        if (!acc[formName]) {
          acc[formName] = [];
        }
        acc[formName].push(submission);
        return acc;
      }, {}),
    [thisFilesSubmissions],
  );

  // --- Time Conversion Helpers ---
  const formatTime = (dateString: string | null | undefined): string => {
    return dateString ? dayjs(dateString).format('MM/DD/YYYY hh:mm A') : 'N/A';
  };

  const parseTimeToDate = (dateString: string | null | undefined): Date | null => {
    if (!dateString) return null;
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.toDate() : null;
  };

  // Handler for tag selection change
  const handleTagsFieldValueChange = useCallback(
    async (newSelectedTags: TagModel[]) => {
      if (!selectedFile || !onUpdateTags) return;

      const newTagIds = newSelectedTags.map((tag) => tag.id);
      // Optimistically update UI. Parent will refetch and send new selectedFile prop.
      setFieldSelectionValue(newSelectedTags);
      setIsSavingTags(true);
      try {
        // Call the parent's update handler
        await onUpdateTags(selectedFile.id, newTagIds);
        // Success/error notifications and data refetching are handled by PatientFiles.tsx
      } catch (error) {
        console.error('Error during onUpdateTags call from PatientFilePreviewEdit:', error);
        // Revert to previously known good state from selectedFile if error occurs locally or parent doesn't refresh.
        const currentTagsFromFile = selectedFile.tags ?? []; // Ensure this uses selectedFile.tags
        setFieldSelectionValue(currentTagsFromFile); // Ensure this uses the direct array
      } finally {
        setIsSavingTags(false);
      }
    },
    [selectedFile, onUpdateTags, tagIdToTagMap], // tagIdToTagMap is a dep for the catch block logic
  );

  // Reset edit state and load current tags/times when selected file changes
  useEffect(() => {
    if (selectedFile) {
      setEditableName(selectedFile.fileName ?? '');
      const currentTagsFromFile = selectedFile.tags ?? [];
      setFieldSelectionValue(currentTagsFromFile);

      setEditableDescription((selectedFile as FileModelWithDescription).description ?? '');
      setIsEditingDescription(false);
      setIsSavingDescription(false);

      setEditableCaptureTime(parseTimeToDate(selectedFile.captureTime));
      setEditableEventTime(parseTimeToDate(selectedFile.eventTime));
      setIsEditingCaptureTime(false);
      setIsEditingEventTime(false);
      setIsSavingCaptureTime(false);
      setIsSavingEventTime(false);

      setIsEditingName(false);
      setIsSavingName(false);
      setIsSavingTags(false);
      setEditingFormState(null);
      originalFormState.current = null;
    } else {
      setEditableName('');
      setFieldSelectionValue([]);
      setEditableDescription('');
      setEditableCaptureTime(null);
      setEditableEventTime(null);
      setIsEditingCaptureTime(false);
      setIsEditingEventTime(false);
      setIsSavingCaptureTime(false);
      setIsSavingEventTime(false);
      setIsEditingName(false);
      setIsSavingName(false);
      setIsEditingDescription(false);
      setIsSavingDescription(false);
      setIsSavingTags(false);
      setEditingFormState(null);
      originalFormState.current = null;
    }
  }, [selectedFile, allHierarchicalTags, areTagsLoading, apiClient]);

  // Accordion handler using simpler type compatible with prop
  const handleAccordionSelect = (eventKey: string | string[] | null | undefined) => {
    if (typeof eventKey === 'string') {
      setActiveKeys((prevKeys) => (prevKeys.includes(eventKey) ? prevKeys.filter((k) => k !== eventKey) : [...prevKeys, eventKey]));
    } else if (Array.isArray(eventKey)) {
      setActiveKeys(eventKey);
    }
  };

  if (!selectedFile) {
    // Optionally render a placeholder when nothing is selected
    return null; // Or <PreviewContainer><PreviewContent>Select a file to preview</PreviewContent></PreviewContainer>
  }

  // Destructure new time fields and user
  const { fileName, fileType, created, fileSize, externalSource, id, captureTime, eventTime, user } = selectedFile;
  const formattedDate = created ? dayjs(created).format('MM/DD/YYYY hh:mm A') : 'N/A';
  const formattedSize = fileSize ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB` : 'N/A';

  const handleViewClick = (e: React.MouseEvent) => {
    if (selectedFile) {
      e.stopPropagation(); // Prevent other clicks if needed
      onView(selectedFile);
    }
  };

  const handleDownloadClick = () => {
    onDownload(selectedFile);
  };

  const handleDeleteClick = () => {
    // Optional: Add confirmation dialog logic here or in the parent
    onDelete(selectedFile);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditableName(event.target.value);
  };

  const handleSaveName = async () => {
    if (!selectedFile || isSavingName) return;
    setIsSavingName(true);
    try {
      await onRename(selectedFile, editableName);
      setIsEditingName(false);
    } catch (error) {
      console.error('Rename failed in preview:', error);
    } finally {
      setIsSavingName(false);
    }
  };

  const toggleEditName = () => {
    if (isEditingName) {
      // If canceling edit, revert name
      setEditableName(selectedFile?.fileName ?? '');
    }
    setIsEditingName(!isEditingName);
  };

  const toggleEditDescription = () => {
    if (isEditingDescription) {
      setEditableDescription((selectedFile as FileModelWithDescription)?.description ?? '');
    }
    setIsEditingDescription(!isEditingDescription);
  };

  const handleSaveDescription = async () => {
    if (!selectedFile || !onUpdateDescription || isSavingDescription) return;
    setIsSavingDescription(true);
    try {
      await onUpdateDescription(selectedFile, editableDescription);
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Description update failed in preview:', error);
      NotificationsService.displayError('Could not update description.');
    } finally {
      setIsSavingDescription(false);
    }
  };

  // --- Timestamp Edit Handlers ---
  const toggleEditCaptureTime = () => {
    if (isEditingCaptureTime) {
      setEditableCaptureTime(parseTimeToDate(selectedFile?.captureTime));
    }
    setIsEditingCaptureTime(!isEditingCaptureTime);
  };

  const toggleEditEventTime = () => {
    if (isEditingEventTime) {
      setEditableEventTime(parseTimeToDate(selectedFile?.eventTime));
    }
    setIsEditingEventTime(!isEditingEventTime);
  };

  const handleSaveTimestamps = async (field: 'capture' | 'event') => {
    if (!selectedFile) return;

    const isSaving = field === 'capture' ? isSavingCaptureTime : isSavingEventTime;
    const setIsSaving = field === 'capture' ? setIsSavingCaptureTime : setIsSavingEventTime;
    const setIsEditing = field === 'capture' ? setIsEditingCaptureTime : setIsEditingEventTime;
    const currentValue = field === 'capture' ? editableCaptureTime : editableEventTime;

    if (isSaving) return;
    setIsSaving(true);

    try {
      const formattedTime = currentValue ? currentValue : null; // Use the Date object directly

      // Call the parent update function, parsing the UNCHANGED time field to Date
      await onUpdateTimestamps(
        selectedFile,
        field === 'capture' ? formattedTime : parseTimeToDate(selectedFile.captureTime),
        field === 'event' ? formattedTime : parseTimeToDate(selectedFile.eventTime),
      );
      setIsEditing(false); // Close edit mode on successful save
    } catch (error) {
      console.error(`Failed to update ${field} time:`, error);
      NotificationsService.displayError(`Could not update ${field} time.`);
      // Optionally revert state here if needed
    } finally {
      setIsSaving(false);
    }
  };

  // --- NEW: Form Edit Handlers ---
  const handleFormEdit = (submission: SubmissionModel) => {
    const submissionCopy = JSON.parse(JSON.stringify(submission));
    setEditingFormState(submissionCopy);
    originalFormState.current = JSON.parse(JSON.stringify(submission));
  };

  const handleFormEditCancel = () => {
    setEditingFormState(null);
    originalFormState.current = null;
  };

  const handleFormFieldChange = (fieldName: string, value: string) => {
    setEditingFormState((currentForm) => {
      if (!currentForm) return null;
      const updatedFields = currentForm.fields.map((field) => (field.name === fieldName ? { ...field, value } : field));
      return { ...currentForm, fields: updatedFields };
    });
  };

  const handleFormSave = async () => {
    if (!editingFormState || !selectedFile) return;

    try {
      setFormSaveInProgress(true);
      const submissionId = editingFormState.id || 0;

      // Ensure all fields have the correct entityId from the selected file.
      const fieldsForSubmission = editingFormState.fields.map((f: DynamicFormFieldModel) => ({
        ...f,
        entityId: selectedFile.id,
      }));

      // Find changed fields to decide if an update is needed and what to send.
      const fieldsToUpdate = fieldsForSubmission.filter((field: DynamicFormFieldModel) => {
        const originalField = originalFormState.current?.fields.find((f: DynamicFormFieldModel) => f.fieldId === field.fieldId);
        return !originalField || originalField.value !== field.value;
      });

      if (fieldsToUpdate.length > 0) {
        if (submissionId) {
          // Existing submission: Call update with only the changed fields.
          await apiClient.dynamicFormsClient.updateDynamicFormSubmission(submissionId, fieldsToUpdate);
          NotificationsService.displaySuccess('Form submission updated successfully.');
        } else {
          // New submission: use setDynamicFormFor
          const newFormSubmission: DynamicFormModel = {
            ...editingFormState,
            entityId: selectedFile.id,
            fields: fieldsForSubmission,
          };

          // These properties are part of SubmissionModel, but not the base DynamicFormModel for creation.
          delete (newFormSubmission as Partial<SubmissionModel>).id;
          delete (newFormSubmission as Partial<SubmissionModel>).dateSubmitted;

          await apiClient.dynamicFormsClient.setDynamicFormFor(newFormSubmission);
          NotificationsService.displaySuccess('Form submission created successfully.');
        }
        await onFormUpdated(); // Refresh data.
      } else {
        NotificationsService.displayInfo('No changes to save.');
      }
    } catch (error) {
      console.error('Failed to save form submission', error);
      NotificationsService.displayError('Failed to save form submission. Please try again.');
    } finally {
      setFormSaveInProgress(false);
      setEditingFormState(null);
      originalFormState.current = null;
    }
  };

  return (
    <PreviewContainer>
      <PreviewContent>
        {/* Smart thumbnail display logic:
            - Show by default (includeThumbnail undefined or true)
            - Hide only when explicitly set to false (e.g., in sidebar when main viewer shows file)
            - Always show when in standalone preview mode */}
        {includeThumbnail !== false && (
          <ThumbnailSection>
            <FileThumbnailDisplay file={selectedFile} />
            {showViewButton && (
              <ViewIconButton onClick={handleViewClick} title="Open Full View">
                <Icon icon={faEye} />
              </ViewIconButton>
            )}
          </ThumbnailSection>
        )}

        {/* File Details Section - conditionally shown */}
        {showFileDetails && (
          <MetadataSection>
            <MetadataItem>
              <strong>File Name:</strong>
              <NameEditContainer>
                {isEditingName && !isReadOnly ? (
                  <Input
                    type="text"
                    value={editableName}
                    onChange={handleNameChange}
                    disabled={isSavingName}
                    style={{ height: '30px', fontSize: '0.9rem' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                    autoFocus
                  />
                ) : (
                  <span>{selectedFile.fileName ?? 'N/A'}</span>
                )}
                {!isReadOnly && (
                  <EditIconWrapper onClick={toggleEditName} title={isEditingName ? 'Cancel Edit' : 'Edit Name'}>
                    <Icon icon={isEditingName ? faXmark : faPencil} />
                  </EditIconWrapper>
                )}
                {isEditingName && !isReadOnly && (
                  <Button size={ComponentSizes.SMALL} onClick={handleSaveName} disabled={isSavingName} style={{ marginLeft: '8px' }} title="Save Name">
                    <Icon icon={faFloppyDisk} />
                  </Button>
                )}
              </NameEditContainer>
            </MetadataItem>
            {/* Other Metadata */}
            <MetadataItem>
              <strong>Type:</strong> <span>{fileType ?? 'N/A'}</span>
            </MetadataItem>

            {/* Event Time */}
            <MetadataItem alignCenter={false}>
              <strong>Event Time:</strong>
              {isEditingEventTime && !isReadOnly ? (
                <TimeEditContainer>
                  <DateTimePicker
                    value={editableEventTime}
                    onChange={(e: DateTimePickerChangeEvent) => setEditableEventTime(e.value)}
                    format="MM/dd/yyyy hh:mm a"
                    disabled={isSavingEventTime}
                  />
                  <EditIconWrapper onClick={toggleEditEventTime} title="Cancel Edit">
                    <Icon icon={faXmark} />
                  </EditIconWrapper>
                  <Button size={ComponentSizes.SMALL} onClick={() => handleSaveTimestamps('event')} disabled={isSavingEventTime} title="Save Event Time">
                    <Icon icon={faFloppyDisk} />
                  </Button>
                </TimeEditContainer>
              ) : (
                <>
                  <span>{formatTime(eventTime)}</span>
                  {!isReadOnly && (
                    <EditIconWrapper onClick={toggleEditEventTime} title="Edit Event Time">
                      <Icon icon={faPencil} />
                    </EditIconWrapper>
                  )}
                </>
              )}
            </MetadataItem>

            {/* Capture Time */}
            <MetadataItem alignCenter={false}>
              <strong>Capture Time:</strong>
              {isEditingCaptureTime && !isReadOnly ? (
                <TimeEditContainer>
                  <DateTimePicker
                    value={editableCaptureTime}
                    onChange={(e: DateTimePickerChangeEvent) => setEditableCaptureTime(e.value)}
                    format="MM/dd/yyyy hh:mm a"
                    disabled={isSavingCaptureTime}
                  />
                  <EditIconWrapper onClick={toggleEditCaptureTime} title="Cancel Edit">
                    <Icon icon={faXmark} />
                  </EditIconWrapper>
                  <Button size={ComponentSizes.SMALL} onClick={() => handleSaveTimestamps('capture')} disabled={isSavingCaptureTime} title="Save Capture Time">
                    <Icon icon={faFloppyDisk} />
                  </Button>
                </TimeEditContainer>
              ) : (
                <>
                  <span>{formatTime(captureTime)}</span>
                  {!isReadOnly && (
                    <EditIconWrapper onClick={toggleEditCaptureTime} title="Edit Capture Time">
                      <Icon icon={faPencil} />
                    </EditIconWrapper>
                  )}
                </>
              )}
            </MetadataItem>
            <MetadataItem>
              <strong>Uploaded:</strong> <span>{formattedDate}</span>
            </MetadataItem>
            {/* Added User */}
            <MetadataItem>
              <strong>Uploaded By:</strong> <span>{user ?? 'N/A'}</span>
            </MetadataItem>
            <MetadataItem>
              <strong>Size:</strong> <span>{formattedSize}</span>
            </MetadataItem>
            <MetadataItem>
              <strong>Source:</strong> <span>{externalSource ? 'External' : 'Direct Upload'}</span>
            </MetadataItem>

            {/* Description */}
            <MetadataItem alignCenter={false}>
              <strong>Description:</strong>
              {!isEditingDescription && (
                <>
                  <span
                    style={{
                      whiteSpace: 'pre-line',
                      flexGrow: 1,
                      paddingRight: '8px',
                      fontStyle: (selectedFile as FileModelWithDescription).description ? 'normal' : 'italic',
                      color: (selectedFile as FileModelWithDescription).description ? 'inherit' : '#6c757d',
                    }}
                  >
                    {(selectedFile as FileModelWithDescription).description || 'No description provided.'}
                  </span>
                  {!isReadOnly && (
                    <EditIconWrapper onClick={toggleEditDescription} title="Edit Description">
                      <Icon icon={faPencil} />
                    </EditIconWrapper>
                  )}
                </>
              )}
            </MetadataItem>
            {isEditingDescription && !isReadOnly && (
              <div style={{ marginTop: '8px' }}>
                <StyledTextArea
                  value={editableDescription}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditableDescription(e.target.value)}
                  disabled={isSavingDescription}
                  rows={5}
                  autoFocus
                />
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
                  <Button size={ComponentSizes.SMALL} onClick={handleSaveDescription} disabled={isSavingDescription} title="Save Description">
                    <Icon icon={faFloppyDisk} />
                  </Button>
                  <Button
                    size={ComponentSizes.SMALL}
                    variant={ButtonVariants.SECONDARY}
                    onClick={toggleEditDescription}
                    disabled={isSavingDescription}
                    title="Cancel"
                  >
                    <Icon icon={faXmark} />
                  </Button>
                </div>
              </div>
            )}
          </MetadataSection>
        )}

        {/* Tags Section - conditionally shown */}
        {showFileDetails && (
          <StyledTagAccordion alwaysOpen onSelect={handleAccordionSelect} activeKey={activeKeys}>
            <Accordion.Item eventKey="tags">
              <Accordion.Header>Tags ({fieldSelectionValue.length})</Accordion.Header>
              <Accordion.Body>
                {areTagsLoading ? (
                  <EditPlaceholder>Loading tags...</EditPlaceholder>
                ) : selectedFile && allHierarchicalTags && allHierarchicalTags.length > 0 ? (
                  <TagsField
                    id={`preview-tags-${selectedFile.id}`}
                    tags={allHierarchicalTags}
                    value={fieldSelectionValue}
                    onChange={isReadOnly ? undefined : handleTagsFieldValueChange}
                    label=""
                  />
                ) : (
                  <EditPlaceholder>
                    {isSavingTags
                      ? 'Saving tags...'
                      : !selectedFile
                        ? 'No file selected.'
                        : !allHierarchicalTags
                          ? 'Tags not available.'
                          : allHierarchicalTags.length === 0
                            ? 'No tags configured in system.'
                            : 'No tags assigned or file not selected.'}
                  </EditPlaceholder>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </StyledTagAccordion>
        )}

        {/* Forms and Fields Section - conditionally shown */}
        {showFileDetails && (
          <StyledFormAccordion alwaysOpen onSelect={handleAccordionSelect} activeKey={activeKeys}>
            <Accordion.Item eventKey="forms">
              <Accordion.Header>Forms and Fields</Accordion.Header>
              <Accordion.Body>
                {areFormSubmissionsLoading ? (
                  <EditPlaceholder>Loading data...</EditPlaceholder>
                ) : thisFilesSubmissions.length > 0 ? (
                  Object.entries(groupedSubmissions).map(([formName, submissions]) => {
                    const latestSubmission = submissions[0];
                    const hasHistory = submissions.length > 1;
                    const isHistoryVisible = historyVisibleFor === formName;
                    const isEditingThis = editingFormState?.id === latestSubmission.id;

                    return (
                      <div key={formName}>
                        <MetadataTitle>{formName}</MetadataTitle>
                        <FormHistoryItem>
                          <FormHistoryHeader>
                            <span>Submitted: {dayjs(latestSubmission.dateSubmitted).format('MM/DD/YYYY hh:mm A')}</span>
                            <FormActions>
                              {isEditingThis ? (
                                <>
                                  <Button size={ComponentSizes.SMALL} onClick={handleFormSave} disabled={formSaveInProgress} title="Save Changes">
                                    <Icon icon={faFloppyDisk} />
                                  </Button>
                                  <Button
                                    size={ComponentSizes.SMALL}
                                    variant={ButtonVariants.SECONDARY}
                                    onClick={handleFormEditCancel}
                                    disabled={formSaveInProgress}
                                    title="Cancel"
                                  >
                                    <Icon icon={faXmark} />
                                  </Button>
                                </>
                              ) : (
                                !isReadOnly && (
                                  <Button
                                    size={ComponentSizes.SMALL}
                                    variant={ButtonVariants.PRIMARY}
                                    onClick={() => handleFormEdit(latestSubmission)}
                                    title="Edit this submission"
                                  >
                                    <Icon icon={faPencil} />
                                  </Button>
                                )
                              )}
                              {hasHistory && (
                                <Button
                                  size={ComponentSizes.SMALL}
                                  variant={ButtonVariants.SECONDARY}
                                  onClick={() => setHistoryVisibleFor(isHistoryVisible ? null : formName)}
                                  style={{ backgroundColor: '#f0f0f0', color: '#333' }}
                                >
                                  {isHistoryVisible ? 'Hide History' : 'View History'}
                                </Button>
                              )}
                            </FormActions>
                          </FormHistoryHeader>
                          <FormHistoryContent>
                            {isEditingThis
                              ? editingFormState?.fields.map((field) => {
                                  const useTextArea = field.value && field.value.length > 50;
                                  const InputComponent = useTextArea ? StyledTextArea : Input;
                                  return (
                                    <FormFieldItem key={field.name}>
                                      <strong>{field.label ?? field.name}:</strong>
                                      <InputComponent
                                        type="text"
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                          handleFormFieldChange(field.name, e.target.value)
                                        }
                                        disabled={formSaveInProgress || isReadOnly}
                                      />
                                    </FormFieldItem>
                                  );
                                })
                              : latestSubmission.fields.map((field) => (
                                  <MetadataItem key={field.name}>
                                    <strong>{field.label ?? field.name}:</strong>
                                    <span>{field.value}</span>
                                  </MetadataItem>
                                ))}
                          </FormHistoryContent>
                        </FormHistoryItem>
                        {isHistoryVisible &&
                          submissions.slice(1).map((submission) => (
                            <FormHistoryItem key={submission.id} style={{ marginLeft: '20px', borderColor: '#adb5bd' }}>
                              <FormHistoryHeader>
                                <span>Submitted: {dayjs(submission.dateSubmitted).format('MM/DD/YYYY hh:mm A')}</span>
                                <FormActions>
                                  {!isReadOnly && (
                                    <Button
                                      size={ComponentSizes.SMALL}
                                      variant={ButtonVariants.PRIMARY}
                                      onClick={() => handleFormEdit(submission)}
                                      title="Edit this submission"
                                    >
                                      <Icon icon={faPencil} />
                                    </Button>
                                  )}
                                </FormActions>
                              </FormHistoryHeader>
                              <FormHistoryContent>
                                {submission.fields.map((field) => (
                                  <MetadataItem key={field.name}>
                                    <strong>{field.label ?? field.name}:</strong>
                                    <span>{field.value}</span>
                                  </MetadataItem>
                                ))}
                              </FormHistoryContent>
                            </FormHistoryItem>
                          ))}
                      </div>
                    );
                  })
                ) : (
                  <EditPlaceholder>No form data available.</EditPlaceholder>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </StyledFormAccordion>
        )}

        {/* Actions Section - conditionally shown with thumbnail gallery */}
        {showThumbnailGallery && (
          <ActionsSection>
            {showViewButton && (
              <Button onClick={handleViewClick} variant={ButtonVariants.PRIMARY} title="View Full File">
                <Icon icon={faEye} /> View
              </Button>
            )}
            {/*<Button variant={ButtonVariants.SECONDARY} onClick={handleDownloadClick} title="Download File">
            <Icon icon={faDownload} /> Download
          </Button>*/}
            {!isReadOnly && (
              <Button variant={ButtonVariants.SECONDARY} onClick={handleDeleteClick} title="Delete File">
                <Icon icon={faTrash} /> Delete
              </Button>
            )}
          </ActionsSection>
        )}
      </PreviewContent>
    </PreviewContainer>
  );
};
